import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import { PageLayout } from './ui';

const SecurityPage = () => {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('logins');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Login data
  const [loginAttempts, setLoginAttempts] = useState([]);
  const [loginStats, setLoginStats] = useState(null);
  const [loginFilter, setLoginFilter] = useState('all'); // all, success, failed, suspicious
  
  // Vulnerability data
  const [pythonVulns, setPythonVulns] = useState(null);
  const [ubuntuUpdates, setUbuntuUpdates] = useState(null);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    if (activeTab === 'logins') {
      loadLoginData();
    } else if (activeTab === 'vulnerabilities') {
      loadVulnerabilityData();
    }
  }, [activeTab]);

  const loadLoginData = async () => {
    try {
      setLoading(true);
      const [attemptsRes, statsRes] = await Promise.all([
        api.get('/security/login-attempts?limit=100&days=7'),
        api.get('/security/login-attempts/stats?days=7')
      ]);
      setLoginAttempts(attemptsRes.data.login_attempts || []);
      setLoginStats(statsRes.data.stats || null);
    } catch (err) {
      setError(err.message || 'Failed to load login data');
    } finally {
      setLoading(false);
    }
  };

  const loadVulnerabilityData = async () => {
    try {
      setLoading(true);
      // Don't auto-scan, just show cached data if available
    } catch (err) {
      setError(err.message || 'Failed to load vulnerability data');
    } finally {
      setLoading(false);
    }
  };

  const scanPythonVulnerabilities = async () => {
    try {
      setScanning(true);
      const res = await api.get('/security/vulnerabilities/python');
      setPythonVulns(res.data);
    } catch (err) {
      setError(err.message || 'Failed to scan Python packages');
    } finally {
      setScanning(false);
    }
  };

  const checkUbuntuUpdates = async () => {
    try {
      setScanning(true);
      const res = await api.get('/security/vulnerabilities/ubuntu');
      setUbuntuUpdates(res.data);
    } catch (err) {
      setError(err.message || 'Failed to check Ubuntu updates');
    } finally {
      setScanning(false);
    }
  };

  const filteredLogins = loginAttempts.filter(attempt => {
    if (loginFilter === 'success') return attempt.success;
    if (loginFilter === 'failed') return !attempt.success;
    if (loginFilter === 'suspicious') return attempt.is_suspicious || attempt.is_new_ip;
    return true;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const tabs = [
    { id: 'logins', label: 'Login Activity', icon: '🔐' },
    { id: 'vulnerabilities', label: 'Vulnerabilities', icon: '🛡️' }
  ];

  const backTo = location.state?.from || '/management';
  const backLabel = backTo === '/tasks' ? 'Back to Tasks' : 'Back to Management';

  return (
    <PageLayout
      title="Security Dashboard"
      subtitle="Monitor login activity and system security"
      backTo={backTo}
      backLabel={backLabel}
    >
      <div className="max-w-7xl mx-auto">
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')} className="text-red-700 font-bold">×</button>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b flex">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Login Activity Tab */}
        {activeTab === 'logins' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            {loginStats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="text-2xl font-bold text-green-600">{loginStats.successful_logins}</div>
                  <div className="text-sm text-gray-500">Successful Logins</div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="text-2xl font-bold text-red-600">{loginStats.failed_logins}</div>
                  <div className="text-sm text-gray-500">Failed Logins</div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="text-2xl font-bold text-primary">{loginStats.new_ip_logins}</div>
                  <div className="text-sm text-gray-500">New IP Logins</div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="text-2xl font-bold text-orange-600">{loginStats.suspicious_logins}</div>
                  <div className="text-sm text-gray-500">Suspicious</div>
                </div>
                <div className="bg-white rounded-lg shadow p-4">
                  <div className="text-2xl font-bold text-purple-600">{loginStats.unique_users}</div>
                  <div className="text-sm text-gray-500">Unique Users</div>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-gray-700">Filter:</span>
                {['all', 'success', 'failed', 'suspicious'].map(filter => (
                  <button
                    key={filter}
                    onClick={() => setLoginFilter(filter)}
                    className={`px-3 py-1 rounded text-sm ${
                      loginFilter === filter
                        ? 'bg-primary text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {filter.charAt(0).toUpperCase() + filter.slice(1)}
                  </button>
                ))}
                <button
                  onClick={loadLoginData}
                  className="ml-auto px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm hover:bg-gray-200"
                >
                  🔄 Refresh
                </button>
              </div>
            </div>

            {/* Login Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b">
                <h2 className="text-lg font-medium">Recent Login Attempts ({filteredLogins.length})</h2>
              </div>
              
              {loading ? (
                <div className="p-6 text-center text-gray-500">Loading...</div>
              ) : filteredLogins.length === 0 ? (
                <div className="p-6 text-center text-gray-500">No login attempts found</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP / Location</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Flags</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredLogins.map(attempt => (
                        <tr key={attempt.id} className={attempt.is_suspicious ? 'bg-red-50' : ''}>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {formatDate(attempt.created_at)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm font-medium">{attempt.user_name || '-'}</div>
                            <div className="text-xs text-gray-500">{attempt.email}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs rounded ${
                              attempt.auth_method === 'microsoft' ? 'bg-primary-light text-primary' :
                              attempt.auth_method === 'google' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {attempt.auth_method || 'local'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm">{attempt.ip_address}</div>
                            <div className="text-xs text-gray-500">
                              {[attempt.city, attempt.country].filter(Boolean).join(', ') || '-'}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {attempt.success ? (
                              <span className="px-2 py-1 text-xs rounded bg-green-100 text-green-800">Success</span>
                            ) : (
                              <span className="px-2 py-1 text-xs rounded bg-red-100 text-red-800">
                                Failed: {attempt.failure_reason || 'unknown'}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1">
                              {attempt.is_new_ip && (
                                <span className="px-2 py-1 text-xs rounded bg-primary-light text-primary">New IP</span>
                              )}
                              {attempt.is_suspicious && (
                                <span className="px-2 py-1 text-xs rounded bg-orange-100 text-orange-800">
                                  ⚠️ {attempt.suspicious_reason || 'Suspicious'}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Vulnerabilities Tab */}
        {activeTab === 'vulnerabilities' && (
          <div className="space-y-6">
            {/* Python Packages */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-medium">🐍 Python Packages</h2>
                  <p className="text-sm text-gray-500">Scan for known vulnerabilities in dependencies</p>
                </div>
                <button
                  onClick={scanPythonVulnerabilities}
                  disabled={scanning}
                  className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-hover disabled:opacity-50"
                >
                  {scanning ? 'Scanning...' : 'Scan Now'}
                </button>
              </div>
              
              <div className="p-6">
                {!pythonVulns ? (
                  <div className="text-center text-gray-500 py-8">
                    <div className="text-4xl mb-2">🔍</div>
                    <p>Click "Scan Now" to check for vulnerabilities</p>
                  </div>
                ) : pythonVulns.vulnerabilities?.length === 0 ? (
                  <div>
                    <div className="text-center text-green-600 py-4">
                      <div className="text-4xl mb-2">✅</div>
                      <p className="font-medium">No vulnerabilities found!</p>
                      <p className="text-sm text-gray-500">Scanned at: {formatDate(pythonVulns.scanned_at)}</p>
                      <p className="text-sm text-gray-500 mt-1">{pythonVulns.summary?.packages_count || 0} packages scanned</p>
                    </div>
                    {pythonVulns.packages?.length > 0 && (
                      <div className="mt-4 border-t pt-4">
                        <h3 className="text-sm font-medium text-gray-700 mb-2">Installed Packages</h3>
                        <div className="max-h-64 overflow-y-auto">
                          <table className="min-w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="px-3 py-2 text-left text-gray-600">Package</th>
                                <th className="px-3 py-2 text-left text-gray-600">Version</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {pythonVulns.packages.map((pkg, idx) => (
                                <tr key={idx} className="hover:bg-gray-50">
                                  <td className="px-3 py-1.5 font-mono text-gray-800">{pkg.name}</td>
                                  <td className="px-3 py-1.5 text-gray-600">{pkg.version}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <div className="flex gap-4 mb-4">
                      <span className="px-3 py-1 bg-red-100 text-red-800 rounded text-sm">
                        High: {pythonVulns.summary?.high || 0}
                      </span>
                      <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded text-sm">
                        Medium: {pythonVulns.summary?.medium || 0}
                      </span>
                      <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded text-sm">
                        Low: {pythonVulns.summary?.low || 0}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {pythonVulns.vulnerabilities?.map((vuln, idx) => (
                        <div key={idx} className="border rounded p-3">
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="font-medium">{vuln.name}</span>
                              <span className="text-gray-500 ml-2">{vuln.version}</span>
                            </div>
                            <span className={`px-2 py-1 text-xs rounded ${
                              vuln.severity === 'HIGH' ? 'bg-red-100 text-red-800' :
                              vuln.severity === 'MEDIUM' ? 'bg-orange-100 text-orange-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {vuln.severity}
                            </span>
                          </div>
                          {vuln.description && (
                            <p className="text-sm text-gray-600 mt-1">{vuln.description}</p>
                          )}
                          {vuln.fix_versions && (
                            <p className="text-sm text-green-600 mt-1">Fix: Upgrade to {vuln.fix_versions.join(' or ')}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Ubuntu Packages */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-medium">🐧 Ubuntu Packages</h2>
                  <p className="text-sm text-gray-500">Check for available security updates</p>
                </div>
                <button
                  onClick={checkUbuntuUpdates}
                  disabled={scanning}
                  className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-hover disabled:opacity-50"
                >
                  {scanning ? 'Checking...' : 'Check Updates'}
                </button>
              </div>
              
              <div className="p-6">
                {!ubuntuUpdates ? (
                  <div className="text-center text-gray-500 py-8">
                    <div className="text-4xl mb-2">🔍</div>
                    <p>Click "Check Updates" to scan for available updates</p>
                  </div>
                ) : ubuntuUpdates.packages?.length === 0 ? (
                  <div className="text-center text-green-600 py-8">
                    <div className="text-4xl mb-2">✅</div>
                    <p className="font-medium">System is up to date!</p>
                    <p className="text-sm text-gray-500">Checked at: {formatDate(ubuntuUpdates.checked_at)}</p>
                  </div>
                ) : (
                  <div>
                    <div className="flex gap-4 mb-4">
                      <span className="px-3 py-1 bg-primary-light text-primary rounded text-sm">
                        Total: {ubuntuUpdates.summary?.total || 0}
                      </span>
                      <span className="px-3 py-1 bg-red-100 text-red-800 rounded text-sm">
                        Security: {ubuntuUpdates.summary?.security || 0}
                      </span>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left">Package</th>
                            <th className="px-3 py-2 text-left">New Version</th>
                            <th className="px-3 py-2 text-left">Type</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {ubuntuUpdates.packages?.map((pkg, idx) => (
                            <tr key={idx}>
                              <td className="px-3 py-2">{pkg.name}</td>
                              <td className="px-3 py-2 text-gray-500">{pkg.version}</td>
                              <td className="px-3 py-2">
                                {pkg.is_security ? (
                                  <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded">Security</span>
                                ) : (
                                  <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">Update</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default SecurityPage;
